import React, { useState } from 'react'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { Select } from 'antd'
import { useStore, View } from '../../store'
import { post } from '~/utils/api'
import {
  ModalBody,
  ModalFooterInForm,
  ModalHeader,
  ModalWrapper,
} from '~/components/common/Modal'
import { TextField, FieldController as Field } from '~/components/common/Form'

type Props = {
  filtersModal: boolean
  setFiltersModal: (editModal: boolean) => void
  setCustomTab: (data: FormValues) => void
}

type FormValues = {
  name: string
  mailAccount: number | null
  tags: any
  reportedBy: number | null
  status: View
  title: string | null
  category: number | null
  subcategory: number | null
  categoryStatus: number | null
}

type Category = {
  id: number
  name: string
}

const { Option } = Select

const Filters: React.FC<Props> = ({
  filtersModal,
  setFiltersModal,
  setCustomTab,
}) => {
  const { addCustomView } = useStore()

  const reportTypes = window.c.ticketReportTypes
  const mailAccounts = window.c.mailAccounts
  const ticketCategories = window.c.ticketCategories

  const [subcategories, setSubcategories] = useState<Category[]>([])
  const [categoryStatuses, setCategoryStatuses] = useState<Category[]>([])

  const close = () => {
    setFiltersModal(false)
    reset()
  }

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<FormValues>()

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    data.tags = data.tags ? '#' + data.tags.join(',' + '#') : null
    const res: any = await post(`tickets/view/add`, data)

    if (res) {
      addCustomView({ ...res, hidden: false })
      setCustomTab(data)
      setFiltersModal(false)
      reset({
        mailAccount: 0,
        reportedBy: 0,
        status: 'open',
        category: null,
        subcategory: null,
        categoryStatus: null,
      })
      setSubcategories([])
      setCategoryStatuses([])
    }
  }

  const changeCategory = e => {
    setValue('category', e)

    if (e) {
      const category = ticketCategories.find(item => item.id == e)

      const subcategories = category?.subcategories
      const statuses = category?.statuses

      setSubcategories(subcategories ? subcategories : [])
      setCategoryStatuses(statuses ? statuses : [])

      subcategories?.length && setValue('status', '')

      !subcategories?.length && setValue('subcategory', null)
      !statuses?.length && setValue('categoryStatus', null)
    } else {
      setSubcategories([])
      setCategoryStatuses([])

      setValue('subcategory', null)
      setValue('categoryStatus', null)
    }
  }

  return (
    <>
      {filtersModal && (
        <ModalWrapper>
          <form
            className="modal-card"
            id="ticket-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ModalHeader
              title={intl.formatMessage({
                id: 'tickets / view / filter / form',
              })}
              onClose={close}
            />
            <ModalBody>
              <Controller
                name={'name'}
                control={control}
                render={renderProps => (
                  <TextField
                    label={intl.formatMessage({
                      id: 'tickets / view / filter / name',
                    })}
                    renderProps={renderProps}
                    horizontal={false}
                    small={true}
                  />
                )}
                rules={{
                  required: intl.formatMessage({
                    id: 'tickets / view / filter / errors / name',
                  }),
                }}
              />

              <Controller
                name={'title'}
                defaultValue={''}
                control={control}
                render={renderProps => (
                  <TextField
                    label={intl.formatMessage({
                      id: 'tickets / view / filter / title',
                    })}
                    renderProps={renderProps}
                    horizontal={false}
                    small={true}
                    required={false}
                  />
                )}
              />

              {ticketCategories.length ? (
                <Controller
                  name="category"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <Field
                      label={intl.formatMessage({
                        id: 'ticket / add / category',
                      })}
                    >
                      <Select
                        {...field}
                        defaultValue={0}
                        className="full-width"
                        onChange={e => changeCategory(e)}
                        placeholder={intl.formatMessage({
                          id: 'ticket / add / category / select',
                        })}
                      >
                        <Option value={0}>–</Option>
                        {ticketCategories.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Field>
                  )}
                />
              ) : null}

              {subcategories.length ? (
                <Controller
                  name="subcategory"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <Field
                      label={intl.formatMessage({
                        id: 'ticket / add / subcategory',
                      })}
                    >
                      <Select
                        {...field}
                        defaultValue={0}
                        className="full-width"
                        placeholder={intl.formatMessage({
                          id: 'ticket / add / subcategory / select',
                        })}
                      >
                        <Option value={0}>–</Option>
                        {subcategories.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Field>
                  )}
                />
              ) : null}

              {categoryStatuses.length ? (
                <Controller
                  name="categoryStatus"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <Field
                      label={intl.formatMessage({
                        id: 'tickets / view / filter / status',
                      })}
                    >
                      <Select
                        {...field}
                        defaultValue={0}
                        className="full-width"
                        placeholder={intl.formatMessage({
                          id: 'tickets / view / filter / status',
                        })}
                      >
                        <Option value={0}>–</Option>
                        {categoryStatuses.map(status => (
                          <Option key={status.id} value={status.id}>
                            {status.name}
                          </Option>
                        ))}
                      </Select>
                    </Field>
                  )}
                />
              ) : null}

              {!categoryStatuses.length && (
                <Controller
                  name="status"
                  control={control}
                  defaultValue={'open'}
                  render={({ field }) => (
                    <Field
                      label={intl.formatMessage({
                        id: 'tickets / view / filter / status',
                      })}
                    >
                      <Select
                        {...field}
                        className="full-width"
                        defaultValue={'open'}
                      >
                        <Option value={'open'}>
                          {intl.formatMessage({
                            id: 'tickets / view / open',
                          })}
                        </Option>
                        <Option value={'todo'}>
                          {intl.formatMessage({
                            id: 'tickets / view / todo',
                          })}
                        </Option>
                        <Option value={'closed'}>
                          {intl.formatMessage({
                            id: 'tickets / view / closed',
                          })}
                        </Option>
                      </Select>
                    </Field>
                  )}
                />
              )}

              <Controller
                name="reportedBy"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <Field
                    label={intl.formatMessage({
                      id: 'tickets / view / filter / reportedBy',
                    })}
                  >
                    <Select {...field} className="full-width" defaultValue={0}>
                      <Option value={0}>–</Option>
                      {Object.entries(reportTypes).map(([key, item]: any) => (
                        <Option key={key} value={key}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Field>
                )}
              />

              {mailAccounts.length ? (
                <Controller
                  name="mailAccount"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <Field
                      label={intl.formatMessage({
                        id: 'tickets / view / filter / email',
                      })}
                    >
                      <Select
                        {...field}
                        className="full-width"
                        defaultValue={0}
                      >
                        <Option value={0}>–</Option>
                        {mailAccounts.map((account, index) => (
                          <Option key={account.id} value={account.id}>
                            {account.address}
                          </Option>
                        ))}
                      </Select>
                    </Field>
                  )}
                />
              ) : (
                ''
              )}

              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Field
                    label={intl.formatMessage({
                      id: 'tickets / view / filter / tags',
                    })}
                  >
                    <Select
                      {...field}
                      mode="tags"
                      className="full-width"
                      notFoundContent={null}
                    />
                  </Field>
                )}
              />
            </ModalBody>
            <ModalFooterInForm onCancel={close} isSubmitting={isSubmitting} />
          </form>
        </ModalWrapper>
      )}
    </>
  )
}

export default Filters
